import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { flatCols } from './useMinOneDayData'
import { useTimeZoneStore } from '../store/useTimeZoneStore'
import { fetchTsIds } from './shared/parallelFetch'
import { useToFill } from './shared/useToFill'
import { isLiveWindow } from '../store/useFillAttemptStore'
import { localDayApiRange, localHourLabel, getOffsetMs } from '../utils/timeUtils'
import { calcHlyTableRange } from './useGrafData'

export function useHlyOneDayData(ghId, date, columns, windowDays = 1, alignEnd = true, isAktual = false, enabled = true) {
  const allCols     = flatCols(columns)
  const tsIds       = allCols.map(c => c.tsId)
  const tz          = useTimeZoneStore(s => s.tz)
  const { begin_date, end_date } = windowDays > 1 || isAktual
    ? calcHlyTableRange(date, windowDays, alignEnd, isAktual, tz)
    : localDayApiRange(date, tz, 23, 0)

  const queryKey = ['hlyOneDayData', ghId, date, tsIds, tz, windowDays, alignEnd, isAktual]

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const responses = await fetchTsIds(
        tsIds,
        tsId => api.get(`/daily_ts?ts_id=${tsId}&gh_id=${ghId}&begin_date=${begin_date}&end_date=${end_date}`),
      )
      const result = Object.fromEntries(tsIds.map((tsId, i) => [tsId, responses[i]?.data ?? []]))
      const toFill = responses.flatMap(r => r?.to_fill ?? [])
      return { result, toFill }
    },
    enabled: !!ghId && enabled,
    staleTime: 5 * 60 * 1000,
  })

  const live = isLiveWindow('HLY', isAktual, new Date(end_date).getTime())
  useToFill(queryKey, data, (result, resp, item) => {
    const tsId = item.params?.ts_id
    if (!tsId || !(tsId in result)) return result
    return { ...result, [tsId]: resp.data }
  }, { gran: 'HLY', live })

  // dataMap: UTC "HH:00" → { tsId: value, ... }
  // labelMap: UTC "HH:00" → local display label
  const dataMap  = new Map()
  const labelMap = new Map()
  const dayMap   = new Map()
  if (data?.result) {
    allCols.forEach(col => {
      ;(data.result[col.tsId] ?? []).forEach(row => {
        if (row.dt == null) return
        const d   = new Date(row.dt)
        const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')} ${String(d.getUTCHours()).padStart(2, '0')}:00`
        const entry = dataMap.get(key) ?? {}
        entry[col.tsId] = row.value ?? null
        dataMap.set(key, entry)
        if (!labelMap.has(key)) {
          labelMap.set(key, localHourLabel(d, tz))
          const local = new Date(d.getTime() + getOffsetMs(d, tz))
          dayMap.set(key, {
            utcDay:   `${d.getUTCDate()}.${d.getUTCMonth() + 1}.`,
            localDay: `${local.getUTCDate()}.${local.getUTCMonth() + 1}.`,
            dateStr:  `${local.getUTCFullYear()}-${String(local.getUTCMonth() + 1).padStart(2, '0')}-${String(local.getUTCDate()).padStart(2, '0')}`,
          })
        }
      })
    })
  }

  return { dataMap, labelMap, dayMap, isLoading, isError }
}
